import type { Dispatch } from "react";
import { useCallback, useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import { RT_EDIT_SETTINGS_STATE } from "app/common/campaigns/KeywordsCampaignConsts";
import { enrichCampaignsWithIcons } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import type {
    IRTCurrentCampaignData,
    RTContextState,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { ECampaignsLoadingState } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { RTContextActions } from "app/pages/digital-marketing/keywords-campaigns/infra/RTContext.consts";
import { ERTContextReducerTypes } from "app/pages/digital-marketing/keywords-campaigns/infra/RTContext.consts";
import {
    KEYWORDS_CAMPAIGN_HOME_STATE,
    RT_CREATE_CAMPAIGN_WIZARD_STATE,
} from "app/pages/digital-marketing/keywords-campaigns/keywords-campaign-homepage/KeywordsCampaignsHomePageConsts";
import { getCampaign } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsCamapaignKeywordsReportTableUtils";

const stateListStatesWithoutCurrentCampaign = [KEYWORDS_CAMPAIGN_HOME_STATE, RT_CREATE_CAMPAIGN_WIZARD_STATE];
const ABORTED_REQUEST_STATUS = 20;
const NON_SENTRY_ERRORS: Array<number> = [403];

const shouldUpdateParamsOnNavigation = (
    routeName: string,
    campaignId: string,
    currentCampaignConfigurationId: string,
): boolean => {
    const shouldUseCurrentCampaign = stateListStatesWithoutCurrentCampaign.every(
        (stateName) => !routeName.includes(stateName),
    );
    const isEditSettingsPage = routeName === RT_EDIT_SETTINGS_STATE;
    const isCurrentCampaignShouldBeUpdated =
        campaignId && currentCampaignConfigurationId !== campaignId && shouldUseCurrentCampaign;

    return isEditSettingsPage || isCurrentCampaignShouldBeUpdated;
};

export const useCurrentCampaign = (
    state: RTContextState,
    dispatch: Dispatch<RTContextActions>,
    isDemoCampaign: boolean,
    isLoadingDemoCampaigns: boolean,
) => {
    const {
        params: { campaignId },
        state: routeState,
    } = useCurrentStateAndParams();
    const [lastCampaignIdError, setLastCampaignIdError] = useState<string | null>(null);
    const { campaignsConfigurations, currentCampaignConfiguration } = state;

    const updateCurrentCampaignAndDuration = useCallback(
        async (isDemoCampaign: boolean, skipLoading = false): Promise<string | undefined> => {
            try {
                if (!skipLoading) {
                    dispatch({
                        type: ERTContextReducerTypes.UpdateLoadingState,
                        payload: { loadingState: ECampaignsLoadingState.LOADING },
                    });
                }

                const currentCampaign = await getCampaign(campaignId, campaignsConfigurations, isDemoCampaign);
                const [campaignWithIcons] = await enrichCampaignsWithIcons<IRTCurrentCampaignData>([currentCampaign]);

                const currentCampaignToSet = isDemoCampaign
                    ? {
                          ...campaignWithIcons,
                          createdTime: dayjs().subtract(3, "month").subtract(1, "days"),
                          isDemo: true,
                      }
                    : campaignWithIcons;

                dispatch({
                    type: ERTContextReducerTypes.UpdateParamsOnNavigation,
                    payload: {
                        campaignConfiguration: currentCampaignToSet,
                    },
                });
                setLastCampaignIdError(null);
                return currentCampaignToSet?.id;
            } catch (error) {
                if (
                    !error.formattedMessage?.includes("Response failed to execute") &&
                    error.status !== ABORTED_REQUEST_STATUS
                ) {
                    if (!NON_SENTRY_ERRORS.includes(error.status)) {
                        Sentry.error(
                            `Error fetching current campaign configurations from server on navigation:`,
                            error,
                        );
                    }

                    setLastCampaignIdError(campaignId);
                    dispatch({
                        type: ERTContextReducerTypes.UpdateCampaignsConfigurationsAndLoadingState,
                        payload: {
                            campaignsConfigurations: null,
                            loadingState: ECampaignsLoadingState.ERROR,
                        },
                    });
                }
            }
        },
        [campaignId, campaignsConfigurations, dispatch],
    );

    const updateLocalCurrentCampaignConfiguration = useCallback(
        (updatedFields: Partial<IRTCurrentCampaignData>) => {
            if (!currentCampaignConfiguration || !updatedFields) {
                return;
            }

            const updatedCampaignConfig = {
                ...currentCampaignConfiguration,
                ...updatedFields,
            };

            dispatch({
                type: ERTContextReducerTypes.UpdateCurrentCampaignConfiguration,
                payload: {
                    campaignConfiguration: updatedCampaignConfig,
                },
            });
        },
        [dispatch, currentCampaignConfiguration],
    );

    useEffect(() => {
        const shouldUpdateParams = shouldUpdateParamsOnNavigation(
            routeState.name,
            campaignId,
            currentCampaignConfiguration?.id,
        );

        const isErrorInCurrentCampaign =
            state.loadingState === ECampaignsLoadingState.ERROR && lastCampaignIdError === campaignId;

        if (
            !isLoadingDemoCampaigns &&
            !isErrorInCurrentCampaign &&
            shouldUpdateParams &&
            (campaignsConfigurations || isDemoCampaign)
        ) {
            void updateCurrentCampaignAndDuration(isDemoCampaign);
        }
    }, [
        campaignId,
        campaignsConfigurations,
        currentCampaignConfiguration?.id,
        dispatch,
        isDemoCampaign,
        routeState.name,
        updateCurrentCampaignAndDuration,
        isLoadingDemoCampaigns,
    ]);

    return {
        reloadCurrentCampaign: useCallback(
            (skipLoading = false) => updateCurrentCampaignAndDuration(isDemoCampaign, skipLoading),
            [isDemoCampaign, updateCurrentCampaignAndDuration],
        ),
        updateLocalCurrentCampaignConfiguration,
    };
};
